import * as RadixAvatar from '@radix-ui/react-avatar';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';

interface Props {
  /** A person's full name, or a vacancy's title — the fallback differs (§4). */
  name: string;
  /** For a vacancy, its abbreviation; for a person, initials are derived from the name. */
  abbreviation?: string;
  src?: string;
  className?: string;
}

/** "Anna Petrova" → "AP"; a single word → its first letter. */
function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return '?';
  }
  return words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * `rounded-full` here is a shape, not a second radius (§3). The fallback sits on `paper-sunken`
 * and is the normal case: this product has no avatar uploads.
 */
export function Avatar({ name, abbreviation, src, className }: Props) {
  const { t } = useTranslation('components');

  return (
    <RadixAvatar.Root
      className={cn(
        'inline-flex size-8 shrink-0 select-none items-center justify-center overflow-hidden rounded-full bg-paper-sunken',
        className,
      )}
    >
      {src && (
        <RadixAvatar.Image
          className="size-full object-cover"
          src={src}
          alt={t('avatar.of', { name })}
        />
      )}
      <RadixAvatar.Fallback
        className="text-xs font-medium text-ink-700"
        aria-label={t('avatar.of', { name })}
      >
        {abbreviation ?? initialsOf(name)}
      </RadixAvatar.Fallback>
    </RadixAvatar.Root>
  );
}
